/**
 * MarqueeTool — SketchUp/CAD rubber-band selection in the Select tool.
 *
 * Left-drag paints a box. Direction sets the rule (AutoCAD/SketchUp
 * convention):
 *   left → right  = WINDOW: only bodies wholly inside the box (solid outline).
 *   right → left  = CROSSING: any body the box touches (dashed outline).
 * Shift adds to the current selection. Alt+left or middle-drag orbit instead
 * (this tool returns null so the camera keeps the gesture).
 *
 * Containment is tested in screen space against each body's world-AABB
 * projected to a screen AABB — cheap and the standard approximation.
 */

import type { CadDocument } from '@core/document';
import type { FeatureId } from '@core/timeline/types';
import type { DragHandler } from '@render/cadControls';
import type { Viewport } from '@render/viewport';

export interface ScreenRect { minX: number; minY: number; maxX: number; maxY: number }

/**
 * Does a body (its screen-space AABB) fall in the marquee? window (crossing=
 * false) requires the body WHOLLY inside; crossing (true) accepts any overlap.
 */
export function boxSelects(body: ScreenRect, marquee: ScreenRect, crossing: boolean): boolean {
  const inside =
    body.minX >= marquee.minX && body.maxX <= marquee.maxX &&
    body.minY >= marquee.minY && body.maxY <= marquee.maxY;
  const intersects =
    body.minX <= marquee.maxX && body.maxX >= marquee.minX &&
    body.minY <= marquee.maxY && body.maxY >= marquee.minY;
  return crossing ? intersects : inside;
}

export class MarqueeTool {
  constructor(
    private readonly viewport: Viewport,
    private readonly host: { readonly doc: CadDocument },
    /** Apply the result: matched ids, additive = shift held at press. */
    private readonly onSelect: (ids: FeatureId[], additive: boolean) => void,
  ) {}

  /** CADControls.onDragStart hook. */
  tryStart = (e: PointerEvent): DragHandler | null => {
    if (e.button !== 0 || e.altKey) return null; // alt/middle → let camera orbit
    const start = { x: e.clientX, y: e.clientY };
    const additive = e.shiftKey;
    let box: HTMLDivElement | null = null; // created lazily on first real move

    const draw = (ev: PointerEvent): void => {
      if (!box) {
        box = document.createElement('div');
        box.className = 'nv-marquee';
        document.body.appendChild(box);
      }
      box.style.left = `${Math.min(start.x, ev.clientX)}px`;
      box.style.top = `${Math.min(start.y, ev.clientY)}px`;
      box.style.width = `${Math.abs(ev.clientX - start.x)}px`;
      box.style.height = `${Math.abs(ev.clientY - start.y)}px`;
      box.classList.toggle('crossing', ev.clientX < start.x); // right→left
    };

    return {
      move: (ev: PointerEvent): void => {
        if (box || Math.hypot(ev.clientX - start.x, ev.clientY - start.y) > 3) draw(ev);
      },
      up: (ev: PointerEvent): void => {
        box?.remove();
        if (!box) return; // never dragged → a plain click; onTap handles it
        const crossing = ev.clientX < start.x;
        const rect: ScreenRect = {
          minX: Math.min(start.x, ev.clientX),
          maxX: Math.max(start.x, ev.clientX),
          minY: Math.min(start.y, ev.clientY),
          maxY: Math.max(start.y, ev.clientY),
        };
        this.onSelect(this.bodiesIn(rect, crossing), additive);
      },
    };
  };

  private bodiesIn(rect: ScreenRect, crossing: boolean): FeatureId[] {
    const report = this.host.doc.lastReport;
    if (!report) return [];
    const out: FeatureId[] = [];
    for (const [id, result] of report.results) {
      if (result.kind !== 'body' || report.consumed.has(id) || result.mesh.positions.length === 0) {
        continue;
      }
      const p = result.mesh.positions;
      let mnx = Infinity, mny = Infinity, mnz = Infinity;
      let mxx = -Infinity, mxy = -Infinity, mxz = -Infinity;
      for (let i = 0; i < p.length; i += 3) {
        mnx = Math.min(mnx, p[i]!); mxx = Math.max(mxx, p[i]!);
        mny = Math.min(mny, p[i + 1]!); mxy = Math.max(mxy, p[i + 1]!);
        mnz = Math.min(mnz, p[i + 2]!); mxz = Math.max(mxz, p[i + 2]!);
      }
      // Project the world AABB's 8 corners → a screen AABB enclosing the body.
      let sMinX = Infinity, sMinY = Infinity, sMaxX = -Infinity, sMaxY = -Infinity;
      let visible = false;
      for (const cx of [mnx, mxx]) for (const cy of [mny, mxy]) for (const cz of [mnz, mxz]) {
        const s = this.viewport.projectToClient(cx, cy, cz);
        if (!s) continue; // corner behind camera
        visible = true;
        sMinX = Math.min(sMinX, s[0]); sMaxX = Math.max(sMaxX, s[0]);
        sMinY = Math.min(sMinY, s[1]); sMaxY = Math.max(sMaxY, s[1]);
      }
      if (!visible) continue;
      const bodyRect: ScreenRect = { minX: sMinX, minY: sMinY, maxX: sMaxX, maxY: sMaxY };
      if (boxSelects(bodyRect, rect, crossing)) out.push(id);
    }
    return out;
  }
}
